import { useRef, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { ChatAttachmentChip } from './ChatAttachmentChip'
import type { ChatAttachment } from '@/types/chat'

interface ChatInlineImageProps {
  attachment: ChatAttachment
  url: string
  onLoadError?: () => void
}

export function ChatInlineImage({ attachment, url, onLoadError }: Readonly<ChatInlineImageProps>) {
  // Tracked per URL, not as a flag: a refreshed download URL replaces an expired one, and the
  // earlier failure must not keep the image pinned to the chip fallback.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  // A refresh mints a new URL, so without this an unrenderable image would fail, ask for a
  // refresh, fail on the new URL, and ask again. One refresh per mount, then the chip stands.
  const refreshRequestedRef = useRef(false)

  const handleLoadError = () => {
    setFailedUrl(url)
    if (refreshRequestedRef.current) return
    refreshRequestedRef.current = true
    onLoadError?.()
  }

  if (failedUrl === url) {
    return <ChatAttachmentChip attachment={attachment} />
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label={`Expand ${attachment.fileName}`}
        className="block max-w-[min(22rem,100%)] overflow-hidden rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="chat-inline-image"
      >
        <img
          src={url}
          alt={attachment.fileName}
          title={attachment.fileName}
          loading="lazy"
          onError={handleLoadError}
          className="max-h-80 max-w-full object-contain"
        />
      </button>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          className="w-auto max-w-[90vw] justify-items-center border-none bg-transparent p-0 shadow-none"
          data-testid="chat-inline-image-viewer"
        >
          <DialogTitle className="sr-only">{attachment.fileName}</DialogTitle>
          <img
            src={url}
            alt={attachment.fileName}
            onError={handleLoadError}
            className="max-h-[85vh] max-w-full rounded-lg object-contain"
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
